import { Component, computed, inject, input, OnInit, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { forkJoin } from 'rxjs';
import {
  FormField,
  disabled,
  form,
  maxLength,
  min,
  pattern,
  required,
} from '@angular/forms/signals';
import { FormsModule } from '@angular/forms';
import { ETipoUnidadSUNAT } from '@core/enums/tipoUnidadSUNAT.enum';
import { unidadesCompatibles, convertirUnidad } from '@core/enums/unidad-compatibles.function';
import { ETipoProducto } from '@core/enums/tipoProducto.enum';
import { IProductoData } from '@core/interfaces/entidades/producto.entidad';
import { IRecetaData } from '@core/interfaces/entidades/receta.entidad';
import {
  IRecetaDetalleReq,
  IEditarRecetaReq,
} from '@core/interfaces/request-response/receta.dto.interface';
import { UiButton, UiDialog, UiInputNumber, UiSelect, type SelectOption } from '@shared/components/ui';
import { DecimalPipe } from '@angular/common';
import { ICategoriaData } from '@core/interfaces/entidades/categoria.entidad';
import { IOptionsSelect } from '@core/interfaces/options.select.interface';
import { TipoUnidadSunatPipe } from '@shared/pipes/tipo-unidad-sunat.pipe';
import { ProductoApiService } from '@core/services/api/producto.service';
import { RecetaApiService } from '@core/services/api/receta-api.service';
import { buildDiff, formatearErrorStock } from '@shared/utils/functions.utils';
import { IEditarProductoReq } from '@core/interfaces/request-response/producto.dto.interface';
import { ProductoImagenService } from '@shared/services/producto-imagen.service';
import { ToastService } from '@shared/services/toast.service';
import { ProductoAvatar } from '@shared/components/producto-avatar/producto-avatar';
import { CrearCategoria } from '../crear-categoria/crear-categoria';
void ProductoAvatar;

@Component({
  selector: 'app-editar-producto',
  imports: [
    FormField,
    FormsModule,
    UiSelect,
    UiInputNumber,
    ProductoAvatar,
    UiButton,
    UiDialog,
    CrearCategoria,
    DecimalPipe,
  ],
  providers: [TipoUnidadSunatPipe],
  templateUrl: './editar-producto.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './editar-producto.css',
})
export class EditarProducto implements OnInit {
  ngOnInit(): void {
    this.opcionesCategorias.set(
      this.categorias().map((categoria) => ({
        label: categoria.nombre,
        value: categoria.id,
      })),
    );
    this.opcionesTipoUnidad.set(
      Object.values(ETipoUnidadSUNAT).map((unidad) => ({
        label: this.tipoUnidadSunatPipe
          .transform(unidad)
          .replace(/_/g, ' ')
          .toLowerCase()
          .replace(/\b\w/g, (c) => c.toUpperCase()),
        value: unidad,
      })),
    );

    this.formProducto().value.set({
      codigo: this.producto().codigo || '',
      nombre: this.producto().nombre || '',
      precio_venta: this.producto().precio_venta || 0,
      precio_base: this.producto().precio_base || 0,
      precio_venta_sucursal: this.producto().stock_sucursal.precio_venta_sucursal ?? null,
      unidad_medida: this.producto().unidad_medida || '',
      stock: this.producto().stock_sucursal.stock || 0,
      categoria_id: this.producto().categoria?.id || null,
      activo: this.producto().activo || false,
      controlar_stock: this.producto().tipo_producto === ETipoProducto.RECETA
        ? false
        : (this.producto().stock_sucursal.controlar_stock || false),
      descripcion: this.producto().descripcion || '',
      es_servicio: !!this.producto().producto_servicio,
      es_gestion_tiempo: this.producto().producto_servicio?.es_gestion_tiempo || false,
      duracion_minutos: this.producto().producto_servicio?.duracion_minutos || 0,
      tipo_producto: this.producto().tipo_producto || ETipoProducto.PRODUCTO,
    });

    if (this.producto().tipo_producto === ETipoProducto.RECETA) {
      this.cargarReceta();
    }
  }

  private tipoUnidadSunatPipe = inject(TipoUnidadSunatPipe);
  private toastService = inject(ToastService);
  private productoApiService = inject(ProductoApiService);
  private productoImagenService = inject(ProductoImagenService);
  private recetaApiService = inject(RecetaApiService);

  productoSignal = signal({
    codigo: '',
    nombre: '',
    descripcion: '',
    precio_venta: 0,
    precio_base: 0,
    precio_venta_sucursal: null as number | null,
    unidad_medida: ETipoUnidadSUNAT.UNIDAD_BIENES,
    stock: 0,
    categoria_id: null as number | null,
    activo: false,
    controlar_stock: false,
    es_servicio: false,
    es_gestion_tiempo: false,
    duracion_minutos: 0,
    tipo_producto: ETipoProducto.PRODUCTO,
  });
  formProducto = form(this.productoSignal, (schemaPath) => {
    required(schemaPath.nombre, { message: 'El nombre es obligatorio.' });
    required(schemaPath.precio_base, { message: 'El precio base es obligatorio.' });
    required(schemaPath.precio_venta, { message: 'El precio de venta es obligatorio.' });
    required(schemaPath.unidad_medida, { message: 'La unidad de medida es obligatoria.' });
    required(schemaPath.stock, { message: 'El stock es obligatorio.' });
    pattern(schemaPath.codigo, /^[a-zA-Z0-9_-]+$/, {
      message: 'El código solo puede contener letras, números, guiones y guiones bajos.',
    });
    maxLength(schemaPath.codigo, 20, {
      message: 'El código no puede exceder los 20 caracteres.',
    });
    maxLength(schemaPath.nombre, 50, {
      message: 'El nombre no puede exceder los 50 caracteres.',
    });
    maxLength(schemaPath.descripcion, 125, {
      message: 'La descripción no puede exceder los 125 caracteres.',
    });
    min(schemaPath.precio_venta, 0, { message: 'El precio de venta no puede ser negativo.' });
    min(schemaPath.precio_base, 0, { message: 'El precio base no puede ser negativo.' });
    min(schemaPath.stock, 0, { message: 'El stock no puede ser negativo.' });

    disabled(schemaPath.controlar_stock, () => {
      return this.formProducto.tipo_producto().value() === ETipoProducto.RECETA;
    });
  });

  opcionesTipoUnidad = signal<IOptionsSelect<ETipoUnidadSUNAT>[]>([]);
  opcionesCategorias = signal<IOptionsSelect<number>[]>([]);
  modalCrearCategoria = signal(false);
  opcionesTipoProducto = signal<IOptionsSelect<ETipoProducto>[]>([
    { label: 'Producto', value: ETipoProducto.PRODUCTO },
    { label: 'Ingrediente', value: ETipoProducto.INGREDIENTE },
    { label: 'Receta', value: ETipoProducto.RECETA },
  ]);

  maxImageSizeMb = signal(this.productoImagenService.defaultMaxSizeMb);
  imagenArchivo = signal<File | null>(null);
  imagenError = signal<string | null>(null);
  imagenDisplayUrl = computed(() => this.producto().url_imagen || null);

  producto = input.required<IProductoData>();
  categorias = input.required<ICategoriaData[]>();
  cerrar = output<IProductoData | undefined>();

  receta = signal<IRecetaData | null>(null);
  ingredientes: {
    producto: IProductoData | null;
    cantidad: number;
    unidad: ETipoUnidadSUNAT;
    costoUnitario: number;
    tempId: number;
  }[] = [];
  private tempIdCounter = 0;
  productosIngrediente = signal<IProductoData[]>([]);

  readonly opcionesProductosIngrediente = computed<SelectOption[]>(() =>
    this.productosIngrediente().map((p) => ({ label: `${p.nombre} (${p.codigo})`, value: p })),
  );

  readonly compareProductoById = (a: any, b: any) => a?.id === b?.id;

  guardandoReceta = signal(false);

  unidadesMedida = [
    { value: ETipoUnidadSUNAT.GRAMO, label: 'Gramos (g)' },
    { value: ETipoUnidadSUNAT.KILOGRAMO, label: 'Kilogramos (kg)' },
    { value: ETipoUnidadSUNAT.MILILITRO, label: 'Mililitros (ml)' },
    { value: ETipoUnidadSUNAT.LITRO, label: 'Litros (L)' },
    { value: ETipoUnidadSUNAT.UNIDAD_BIENES, label: 'Unidades (u)' },
  ];

  unidadesCompatiblesParaIngrediente(ing: {
    producto: IProductoData | null;
    unidad: ETipoUnidadSUNAT;
  }): { value: ETipoUnidadSUNAT; label: string }[] {
    const ref = ing.producto?.unidad_medida || ing.unidad;
    const compatibles = unidadesCompatibles(ref);
    return this.unidadesMedida.filter((u) => compatibles.includes(u.value));
  }

  isTipoProducto = computed(() => {
    return this.formProducto.tipo_producto().value() === ETipoProducto.PRODUCTO;
  });

  isTipoReceta = computed(() => {
    return this.formProducto.tipo_producto().value() === ETipoProducto.RECETA;
  });

  onTipoProductoChange(value: ETipoProducto) {
    if (value === ETipoProducto.RECETA) {
      this.formProducto.controlar_stock().value.set(false);
    } else {
      this.formProducto.controlar_stock().value.set(this.producto().stock_sucursal.controlar_stock);
    }
  }

  cargarReceta() {
    forkJoin([
      this.recetaApiService.obtenerRecetas(),
      this.productoApiService.paginar(1, 200, undefined, ETipoProducto.INGREDIENTE),
    ]).subscribe({
      next: ([recetas, ingredientesRes]) => {
        this.productosIngrediente.set(ingredientesRes.data.filter((p) => p.activo));
        const recetaEncontrada = recetas.find((r) => r.producto_receta_id === this.producto().id);
        if (recetaEncontrada) {
          this.receta.set(recetaEncontrada);
          this.ingredientes = recetaEncontrada.detalles.map((d) => {
            const productoMatch =
              this.productosIngrediente().find((p) => p.id === d.ingrediente_producto_id) ||
              ({
                id: d.ingrediente_producto_id,
                nombre: d.ingrediente_nombre,
                codigo: d.ingrediente_codigo,
                precio_base: d.costo_unitario,
                unidad_medida: d.unidad_medida,
              } as IProductoData);
            return {
              producto: productoMatch,
              cantidad: d.cantidad_requerida,
              unidad: d.unidad_medida,
              costoUnitario: d.costo_unitario,
              tempId: this.tempIdCounter++,
            };
          });
        }
      },
      error: () => {},
    });
  }

  cargarProductosIngrediente() {
    if (this.productosIngrediente().length > 0) return;
    this.productoApiService.paginar(1, 200, undefined, ETipoProducto.INGREDIENTE).subscribe({
      next: (r) => this.productosIngrediente.set(r.data.filter((p) => p.activo)),
    });
  }

  agregarIngrediente() {
    this.cargarProductosIngrediente();
    this.ingredientes.push({
      producto: null,
      cantidad: 0,
      unidad: ETipoUnidadSUNAT.GRAMO,
      costoUnitario: 0,
      tempId: this.tempIdCounter++,
    });
  }

  removerIngrediente(index: number) {
    this.ingredientes.splice(index, 1);
  }

  onIngredienteSeleccionado(index: number) {
    const ing = this.ingredientes[index];
    if (ing.producto) {
      ing.unidad = ing.producto.unidad_medida;
      this.actualizarCostoIngrediente(index);
    }
  }

  onUnidadIngredienteCambiada(index: number) {
    this.actualizarCostoIngrediente(index);
  }

  private actualizarCostoIngrediente(index: number) {
    const ing = this.ingredientes[index];
    if (!ing.producto) return;
    const factor = convertirUnidad(1, ing.unidad, ing.producto.unidad_medida);
    ing.costoUnitario = ing.producto.precio_base * factor;
  }

  costoTotal(): number {
    return this.ingredientes.reduce(
      (sum, ing) => sum + (ing.costoUnitario || 0) * (ing.cantidad || 0),
      0,
    );
  }

  guardarReceta() {
    if (!this.receta()) {
      this.toastService.show('top-right', 'error', 'Error', 'No se encontró la receta asociada.');
      return;
    }
    if (this.ingredientes.length === 0) {
      this.toastService.show('top-right', 'error', 'Error', 'Agrega al menos un ingrediente.');
      return;
    }
    for (let i = 0; i < this.ingredientes.length; i++) {
      if (!this.ingredientes[i].producto?.id) {
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          `Selecciona un ingrediente en la fila ${i + 1}.`,
        );
        return;
      }
      if (!this.ingredientes[i].cantidad || this.ingredientes[i].cantidad <= 0) {
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          `La cantidad del ingrediente ${i + 1} debe ser mayor a 0.`,
        );
        return;
      }
    }

    const detallesReq: IRecetaDetalleReq[] = this.ingredientes.map((ing) => ({
      ingrediente_producto_id: ing.producto!.id,
      cantidad_requerida: ing.cantidad,
      unidad_medida: ing.unidad,
    }));

    const req: IEditarRecetaReq = { detalles: detallesReq };
    this.guardandoReceta.set(true);
    this.recetaApiService.editar(this.receta()!.producto_receta_id, req).subscribe({
      next: (recetaActualizada) => {
        this.guardandoReceta.set(false);
        this.receta.set(recetaActualizada);
        const productos = this.productosIngrediente();
        this.ingredientes = recetaActualizada.detalles.map((d) => {
          const productoMatch =
            productos.find((p) => p.id === d.ingrediente_producto_id) ||
            ({
              id: d.ingrediente_producto_id,
              nombre: d.ingrediente_nombre,
              codigo: d.ingrediente_codigo,
              precio_base: d.costo_unitario,
              unidad_medida: d.unidad_medida,
            } as IProductoData);
          return {
            producto: productoMatch,
            cantidad: d.cantidad_requerida,
            unidad: d.unidad_medida,
            costoUnitario: d.costo_unitario,
            tempId: this.tempIdCounter++,
          };
        });
        this.toastService.show(
          'top-right',
          'success',
          'Éxito',
          'Receta actualizada correctamente.',
        );
      },
      error: (err) => {
        this.guardandoReceta.set(false);
        if (err.status == 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error.message
            ? formatearErrorStock(err.error.message)
            : 'Error al actualizar la receta.',
        );
      },
    });
  }

  onChildImagenChange(file: File | null) {
    this.imagenArchivo.set(file);
    if (!file) this.imagenError.set(null);
  }

  onGuardar($event: Event) {
    $event.preventDefault();
    if (this.imagenError()) {
      this.toastService.show('top-right', 'error', 'Error', this.imagenError()!);
      return;
    }
    const producto = this.producto();
    const fv = this.formProducto().controlValue();

    const original: IEditarProductoReq = {
      codigo: producto.codigo,
      nombre: producto.nombre,
      descripcion: producto.descripcion,
      precio_venta: producto.precio_venta,
      precio_base: producto.precio_base,
      precio_venta_sucursal: producto.stock_sucursal?.precio_venta_sucursal ?? null,
      stock: producto.stock_sucursal.stock,
      categoria_id: producto.categoria?.id || null,
      unidad_medida: producto.unidad_medida,
      controlar_stock: producto.tipo_producto === ETipoProducto.RECETA
        ? false
        : (producto.stock_sucursal.controlar_stock || false),
      activo: producto.activo,
      es_servicio: !!producto.producto_servicio,
      es_gestion_tiempo: producto.producto_servicio?.es_gestion_tiempo ?? false,
      duracion_minutos: producto.producto_servicio?.duracion_minutos ?? 0,
      tipo_producto: producto.tipo_producto || ETipoProducto.PRODUCTO,
    };

    const current: Partial<IEditarProductoReq> = {
      ...fv,
      categoria_id: fv.categoria_id || null,
      precio_venta_sucursal: fv.precio_venta_sucursal || null,
      es_servicio: fv.es_servicio,
      es_gestion_tiempo: fv.es_gestion_tiempo,
      duracion_minutos: fv.es_gestion_tiempo ? fv.duracion_minutos : undefined,
      tipo_producto: fv.tipo_producto,
    };

    const dataToSave: Partial<IEditarProductoReq> = buildDiff<IEditarProductoReq>(
      original,
      current,
    );
    const hasImage = !!this.imagenArchivo();
    if (!Object.keys(dataToSave).length && !hasImage) {
      this.toastService.show('top-right', 'info', 'Sin cambios', 'No se han realizado cambios.');
      return;
    }

    const payload = hasImage
      ? this.productoImagenService.buildFormData(dataToSave, {
          file: this.imagenArchivo()!,
          fileFieldName: 'imagen',
          nullAsEmptyKeys: ['categoria_id'],
        })
      : dataToSave;

    this.productoApiService.guardar(this.producto().id, payload).subscribe({
      next: (data) => {
        this.cerrar.emit(data);
        this.toastService.show(
          'top-right',
          'success',
          'Éxito',
          'Producto actualizado correctamente.',
        );
      },
      error: (err) => {
        if (err.status == 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error.message || 'Error al guardar el producto',
        );
      },
    });
  }

  onCerrar() {
    this.cerrar.emit(undefined);
  }

  onCrearCategoriaCerrada(data?: ICategoriaData) {
    this.modalCrearCategoria.set(false);
    if (data) {
      this.opcionesCategorias.update((opts) => [...opts, { label: data.nombre, value: data.id }]);
      this.formProducto.categoria_id().value.set(data.id);
    }
  }
}